import { useEffect, useRef } from "react";
import { scrollToTarget } from "../../../../common-deprecated/utils/scrollUtils";
import { useCarConfigDispatch, useCarConfigSelector } from "../redux/store";
import type { CarConfigConfigureSection, CarConfigHighlightSection } from "../utils/types/SectionTypes";
import type { CarConfigConfigureSectionsLbx } from "../utils/types/SectionTypesLbx";
import { handlePassedSection, setActiveSection } from "../redux/slices/CarConfigNavigationSlice";
import {
    getActiveSectionFromStorage,
    getSessionStorageParams,
    setActiveSectionToStorage,
} from "../utils/sessionStorage";
import type { CarConfigStep } from "../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";

const useSectionIntersectionObserver = <
    T extends CarConfigConfigureSection | CarConfigConfigureSectionsLbx | CarConfigHighlightSection,
>(
    sectionRefs: Map<T, React.RefObject<HTMLElement>>,
): void => {
    const dispatch = useCarConfigDispatch();
    const sessionStorageParams = useCarConfigSelector(getSessionStorageParams);
    const modelId = useCarConfigSelector((state) => state.car.model.id);
    // This intersectionObserver is only used in the Configure and Highlights page so the type is cast here
    const activeStep = useCarConfigSelector((state) => state.carConfigNavigation.step) as Exclude<
        CarConfigStep,
        CarConfigStep.Specs | CarConfigStep.SpecsAndHighlights
    >;

    const currentSection = useRef<string>();

    const getActiveSections = (sections: Record<string, number>): void => {
        // Get sections that are actually visible (intersection ratio > 0)
        const visibleSections = Object.entries(sections).filter(([, ratio]) => ratio > 0);

        if (visibleSections.length === 0) {
            return;
        }

        // Check if we're at the very top of the page
        const isAtTop = window.scrollY < 100; // Adjust threshold as needed

        let newSection: string;

        if (isAtTop) {
            // When at the top, prefer the first visible section in DOM order
            const sectionElements: { id: string; top: number; ratio: number }[] = [];

            visibleSections.forEach(([sectionId, ratio]) => {
                const element = document.getElementById(sectionId);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    sectionElements.push({
                        id: sectionId,
                        top: rect.top,
                        ratio,
                    });
                }
            });

            // Sort by position in DOM and pick the first one that's actually visible
            sectionElements.sort((a, b) => a.top - b.top);
            const [firstVisibleSection] = sectionElements.filter((section) => section.ratio > 0);
            newSection = firstVisibleSection?.id || visibleSections[0][0];
        } else {
            // Normal behavior: section with highest intersection ratio
            const [highestSection] = visibleSections.reduce((a, b) => (sections[a[0]] > sections[b[0]] ? a : b));
            newSection = highestSection;
        }

        // Only overwrite the states activeSection if the newSection differs from the local currentSection
        if (currentSection.current !== newSection) {
            currentSection.current = newSection;
            dispatch(setActiveSection(newSection as T));
            dispatch(handlePassedSection({ newSection: newSection as T, sectionsWithIntersectionRatio: sections }));
            setActiveSectionToStorage(sessionStorageParams, newSection as T, activeStep, modelId);
            window.history.replaceState(window.history.state, "", `#${newSection}`);
        }
    };

    useEffect(() => {
        const activeSessionSection = getActiveSectionFromStorage(sessionStorageParams, activeStep, modelId);

        if (activeSessionSection) {
            scrollToTarget(activeSessionSection);
        }

        const observer = new IntersectionObserver(
            (entries) => {
                // Loop over all entries to keep track of their intersectionRatio.
                entries.forEach((entry) => {
                    sections[entry.target.id] = entry.intersectionRatio * 100;
                });

                getActiveSections(sections);
            },
            { threshold: [0.0, 0.2, 0.4, 0.6, 0.8, 1.0], rootMargin: "0% 0% -35% 0%" },
        );

        // Loop through the collection of all the section refs and observes
        sectionRefs.forEach((sectionRef) => {
            if (sectionRef.current) {
                observer.observe(sectionRef.current as HTMLElement);
            }
        });

        const getInitial = (): Record<string, number> => {
            const sections: Record<string, number> = {};

            sectionRefs.forEach((sectionRef) => {
                if (observer && sectionRef.current) {
                    sections[sectionRef.current.id] = 0; // Initial intersectionRatio
                }
            });

            return sections;
        };

        const sections = getInitial();

        return () => {
            sectionRefs.forEach((sectionRef) => {
                if (sectionRef.current) {
                    observer.unobserve(sectionRef.current as HTMLElement);
                }
            });
            observer.disconnect();
        };
    }, [sectionRefs.size]);
};

export default useSectionIntersectionObserver;
